/**
 * Compute evaluator — evaluates a Compute DSL node to produce a value.
 */

import type { Compute } from '../types/compute';
import type { ConditionContext } from './conditionEvaluator';
import { evaluateCondition } from './conditionEvaluator';
import { applyMathPrecision } from '../precision/precisionContract';
import { parseDateLocal } from '@packages/core';

function toLocalIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface ComputeContext {
  fieldValue?: unknown;
  values: Record<string, unknown>;
  meta: Record<string, unknown>;
  getOptionProp?: (fieldName: string, prop: string) => unknown;
  customResolvers?: Record<string, (context: ComputeContext) => unknown>;
}

export function evaluateCompute(
  compute: Compute,
  context: ComputeContext,
): unknown {
  const conditionContext: ConditionContext = {
    fieldValue: context.fieldValue,
    values: context.values,
    meta: context.meta,
    getOptionProp: context.getOptionProp,
  };

  switch (compute.operator) {
    // ── Literal ──────────────────────────────────────────────────

    case 'value':
      return compute.value;

    case 'today':
      return toLocalIsoDate(new Date());

    case 'dateOffset': {
      let date: Date;
      if (compute.source) {
        const src = typeof compute.source === 'string'
          ? getNestedValue(context.values, compute.source)
          : evaluateCompute(compute.source as Compute, context);
        date = src ? parseDateLocal(String(src)) : new Date();
      } else if (context.fieldValue != null && context.fieldValue !== '') {
        const parsed = parseDateLocal(String(context.fieldValue));
        date = isNaN(parsed.getTime()) ? new Date() : parsed;
      } else {
        date = new Date();
      }
      if (isNaN(date.getTime())) return null;
      const sign = compute.negate ? -1 : 1;
      const days = compute.days != null ? resolveNumeric(compute.days, context) : 0;
      const months = compute.months != null ? resolveNumeric(compute.months, context) : 0;
      const years = compute.years != null ? resolveNumeric(compute.years, context) : 0;
      if (years !== 0) date.setFullYear(date.getFullYear() + years * sign);
      if (months !== 0) date.setMonth(date.getMonth() + months * sign);
      if (days !== 0) date.setDate(date.getDate() + days * sign);
      return toLocalIsoDate(date);
    }

    // ── Lookup ──────────────────────────────────────────────────

    case 'field':
      return getNestedValue(context.values, compute.name);

    case 'optionProp':
      return context.getOptionProp?.(compute.field, compute.prop) ?? null;

    // ── Logic ───────────────────────────────────────────────────

    case 'ifElse': {
      const result = evaluateCondition(compute.condition, conditionContext);
      const branch = result ? compute.then : compute.else;
      return resolveComputeBranch(branch, context);
    }

    case 'not':
      return !evaluateCondition(compute.condition, conditionContext);

    case 'isEmpty':
      return isEmpty(context.fieldValue);

    case 'coalesce': {
      for (const fieldPath of compute.fields) {
        const val = getNestedValue(context.values, fieldPath);
        if (!isEmpty(val)) return val;
      }
      return compute.fallback ?? null;
    }

    case 'switch': {
      const discriminant = compute.on
        ? String(getNestedValue(context.values, compute.on) ?? '')
        : String(context.fieldValue ?? '');
      return discriminant in compute.cases
        ? compute.cases[discriminant]
        : (compute.default ?? null);
    }

    // ── Math ────────────────────────────────────────────────────

    case 'add': {
      const sum = compute.operands.reduce<number>(
        (acc, op) => acc + resolveNumeric(op, context),
        0,
      );
      return applyMathPrecision(sum, compute.precision);
    }

    case 'subtract': {
      const [first, ...rest] = compute.operands;
      const diff = rest.reduce<number>(
        (acc, op) => acc - resolveNumeric(op, context),
        resolveNumeric(first, context),
      );
      return applyMathPrecision(diff, compute.precision);
    }

    case 'multiply': {
      const product = compute.operands.reduce<number>(
        (acc, op) => acc * resolveNumeric(op, context),
        1,
      );
      return applyMathPrecision(product, compute.precision);
    }

    case 'divide': {
      const divisor = resolveNumeric(compute.divisor, context);
      if (divisor === 0) return null;
      const quotient = resolveNumeric(compute.dividend, context) / divisor;
      return applyMathPrecision(quotient, compute.precision);
    }

    case 'round': {
      const src = compute.source !== undefined
        ? resolveNumeric(compute.source, context)
        : Number(context.fieldValue);
      if (isNaN(src)) return null;
      const factor = Math.pow(10, compute.decimals ?? 0);
      const mode = compute.mode ?? 'round';
      if (mode === 'floor') return Math.floor(src * factor) / factor;
      if (mode === 'ceil') return Math.ceil(src * factor) / factor;
      return Math.round(src * factor) / factor;
    }

    case 'min':
      return Math.min(...compute.operands.map((o) => resolveNumeric(o, context)));

    case 'max':
      return Math.max(...compute.operands.map((o) => resolveNumeric(o, context)));

    case 'clamp': {
      const val = compute.source !== undefined
        ? resolveNumeric(compute.source, context)
        : Number(context.fieldValue);
      if (isNaN(val)) return null;
      return Math.min(Math.max(val, compute.min), compute.max);
    }

    case 'percent': {
      const whole = resolveNumeric(compute.whole, context);
      if (whole === 0) return null;
      const pct = (resolveNumeric(compute.part, context) / whole) * 100;
      const decimals = compute.decimals ?? undefined;
      return applyMathPrecision(pct, decimals);
    }

    // ── String ──────────────────────────────────────────────────

    case 'concat': {
      const sep = compute.separator ?? '';
      return compute.parts
        .map((p) => {
          if (typeof p === 'string') return p;
          const val = getNestedValue(context.values, p.field);
          return val != null ? String(val) : '';
        })
        .filter((s) => s !== '')
        .join(sep);
    }

    case 'template':
      return compute.template.replace(/\{([^}]+)\}/g, (_, fieldPath: string) => {
        const val = getNestedValue(context.values, fieldPath.trim());
        return val != null ? String(val) : '';
      });

    case 'toUpperCase':
      return typeof context.fieldValue === 'string'
        ? context.fieldValue.toUpperCase()
        : context.fieldValue;

    case 'toLowerCase':
      return typeof context.fieldValue === 'string'
        ? context.fieldValue.toLowerCase()
        : context.fieldValue;

    case 'trim':
      return typeof context.fieldValue === 'string'
        ? context.fieldValue.trim()
        : context.fieldValue;

    case 'substring':
      return typeof context.fieldValue === 'string'
        ? context.fieldValue.substring(compute.start, compute.end)
        : context.fieldValue;

    // ── Array ───────────────────────────────────────────────────

    case 'appendUnique': {
      const arr = getNestedValue(context.values, compute.target);
      const current: unknown[] = Array.isArray(arr) ? [...arr] : [];
      const item = context.fieldValue;
      if (item != null && item !== '' && !current.includes(item)) {
        current.push(item);
      }
      return current;
    }

    case 'removeFromArray': {
      const arr = getNestedValue(context.values, compute.target);
      if (!Array.isArray(arr)) return [];
      const item = context.fieldValue;
      return arr.filter((v) => v !== item);
    }

    case 'arrayLength': {
      const source = compute.source
        ? getNestedValue(context.values, compute.source)
        : context.fieldValue;
      return Array.isArray(source) ? source.length : 0;
    }

    case 'arrayMap': {
      const arr = getNestedValue(context.values, compute.source);
      if (!Array.isArray(arr)) return [];
      return arr.map((item) =>
        item != null && typeof item === 'object'
          ? (item as Record<string, unknown>)[compute.pluck]
          : undefined,
      );
    }

    case 'arrayFilter': {
      const arr = getNestedValue(context.values, compute.source);
      if (!Array.isArray(arr)) return [];
      return arr.filter((item) =>
        item != null &&
        typeof item === 'object' &&
        (item as Record<string, unknown>)[compute.where.key] === compute.where.equals,
      );
    }

    case 'runningTotal': {
      const parts = compute.source.split('.');
      if (parts.length < 2) return 0;
      const fieldName = parts.pop()!;
      const arrayPath = parts.join('.');
      const arr = getNestedValue(context.values, arrayPath);
      if (!Array.isArray(arr) || arr.length === 0) return 0;

      const endIndex = compute.upToIndex ?? arr.length - 1;
      let sum = 0;
      for (let i = 0; i <= endIndex && i < arr.length; i++) {
        const item = arr[i];
        if (item == null || typeof item !== 'object') continue;
        sum += resolveNumeric((item as Record<string, unknown>)[fieldName], context);
      }
      return sum;
    }

    // ── Date ────────────────────────────────────────────────────

    case 'dateDiff': {
      const fromVal = getNestedValue(context.values, compute.from);
      const toVal = getNestedValue(context.values, compute.to);
      if (!fromVal || !toVal) return null;
      const fromDate = parseDateLocal(String(fromVal));
      const toDate = parseDateLocal(String(toVal));
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return null;
      const diffMs = toDate.getTime() - fromDate.getTime();
      switch (compute.unit) {
        case 'days':
          return Math.floor(diffMs / (1000 * 60 * 60 * 24));
        case 'months':
          return (toDate.getFullYear() - fromDate.getFullYear()) * 12 +
            (toDate.getMonth() - fromDate.getMonth());
        case 'years':
          return toDate.getFullYear() - fromDate.getFullYear();
      }
      return null;
    }

    case 'formatDate': {
      const src = compute.source
        ? getNestedValue(context.values, compute.source)
        : context.fieldValue;
      if (!src) return null;
      const d = parseDateLocal(String(src));
      if (isNaN(d.getTime())) return null;
      switch (compute.format) {
        case 'iso':
          return d.toISOString();
        case 'yyyy-mm-dd':
          return toLocalIsoDate(d);
        case 'short':
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        case 'long':
          return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      }
      return null;
    }

    // ── Type coercion ───────────────────────────────────────────

    case 'toNumber': {
      const num = Number(context.fieldValue);
      return isNaN(num) ? (compute.fallback ?? null) : num;
    }

    case 'toString':
      return context.fieldValue != null ? String(context.fieldValue) : '';

    case 'toBoolean':
      return Boolean(context.fieldValue) &&
        context.fieldValue !== '0' &&
        context.fieldValue !== 'false';

    // ── Compose ─────────────────────────────────────────────────

    case 'pipe': {
      let current = context.fieldValue;
      for (const step of compute.steps) {
        current = evaluateCompute(step, { ...context, fieldValue: current });
      }
      return current;
    }

    // ── Escape hatch ────────────────────────────────────────────

    case 'custom': {
      const resolver = context.customResolvers?.[compute.id];
      if (!resolver) {
        console.warn(`[form-engine] Unknown custom compute resolver: ${compute.id}`);
        return null;
      }
      return resolver(context);
    }

    default: {
      const _exhaustive: never = compute;
      console.warn(`[form-engine] Unknown compute operator: ${(_exhaustive as Compute).operator}`);
      return null;
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────

function isComputeBranch(value: unknown): value is Compute {
  return (
    typeof value === 'object' &&
    value !== null &&
    'operator' in value &&
    typeof (value as { operator: unknown }).operator === 'string'
  );
}

function resolveComputeBranch(branch: unknown, context: ComputeContext): unknown {
  return isComputeBranch(branch) ? evaluateCompute(branch, context) : branch;
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  if (typeof path !== 'string') {
    console.warn('[form-engine] getNestedValue received non-string path:', typeof path);
    return undefined;
  }
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function resolveNumeric(operand: string | number, context: ComputeContext): number {
  if (typeof operand === 'number') return isNaN(operand) ? 0 : operand;
  const val = getNestedValue(context.values, operand);
  if (val == null || val === '') return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (typeof val === 'string') {
    const stripped = val.trim().replace(/,/g, '');
    if (stripped === '') return 0;
    const num = Number(stripped);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}
